// Pure WST-319 access predicates (no React/auth imports, so any test can load them; the hook is in clinicAccess.ts).
// Mirrors provider.access.clinic_access_payload (users/me `clinic_access`).
export type ClinicAccess = {
  centralized: boolean;
  is_owner: boolean;
  is_admin: boolean;
  clinic_view: boolean;
  clinic_wide: boolean;
  clinical?: boolean; // false = clinic staff who is not a provider
  roles: { provider_access: 'own_clients' | 'supervisor' | 'entire_clinic'; manager: boolean; biller: boolean; scheduler: boolean };
  permissions: string[];
  supervises: { providers: string[]; permissions: string[] };
};

export type PermScope = 'own' | 'supervised' | 'all';

export type RolesCatalog = {
  groups: { label: string; permissions: { key: string; label: string }[] }[];
  roles: {
    key: string;
    label: string;
    section: 'admin' | 'clinical' | 'administrative';
    kind: 'admin' | 'level' | 'addon';
    description: string;
    permissions: Record<string, PermScope>;
  }[];
};

type Uuid = string | number | null; // models type uuids as ID
type UserLike = { provider?: { uuid?: Uuid; is_admin?: boolean; payment_details_enabled?: boolean } | null } | undefined;
type ProviderLike = { uuid?: Uuid } | undefined;

export const DEFAULT_ACCESS: ClinicAccess = {
  centralized: false,
  is_owner: false,
  is_admin: false,
  clinic_view: false,
  clinic_wide: false,
  roles: { provider_access: 'own_clients', manager: false, biller: false, scheduler: false },
  permissions: [],
  supervises: { providers: [], permissions: [] },
};

export const can = (access: ClinicAccess, key: string): boolean => access.permissions.includes(key);

// A missing currentProvider (not loaded yet) counts as self, so todayGate keeps deciding and nothing flickers.
export const actingAsSelf = (currentUser: UserLike, currentProvider: ProviderLike): boolean =>
  !currentProvider?.uuid || currentProvider.uuid === currentUser?.provider?.uuid;

// `todayGate` is the site's existing check; it still decides whenever it did before (decentralized, or acting as self).
export const canInContext = (
  access: ClinicAccess,
  key: string,
  currentUser: UserLike,
  currentProvider: ProviderLike,
  todayGate: boolean
): boolean => {
  if (!access.centralized || actingAsSelf(currentUser, currentProvider)) return todayGate;
  return (
    can(access, key) ||
    (!!currentProvider?.uuid && access.supervises.providers.includes(String(currentProvider.uuid)) && access.supervises.permissions.includes(key))
  );
};

export const canClinicView = (access: ClinicAccess): boolean => access.clinic_view;
export const canActAsClinic = (access: ClinicAccess): boolean => access.clinic_wide;
export const canSeeMoney = (currentUser: UserLike, _currentProvider?: ProviderLike): boolean =>
  !!(currentUser?.provider?.is_admin || currentUser?.provider?.payment_details_enabled);
export const canManageRoles = (access: ClinicAccess): boolean => access.is_admin;
export const canManageAdmins = (access: ClinicAccess): boolean => access.is_owner;

const RANK: Record<PermScope, number> = { own: 1, supervised: 2, all: 3 };

// Union of the chosen role keys' catalog permissions; the wider scope wins.
export const previewPermissions = (catalog: RolesCatalog, roles: string[]): Record<string, PermScope> => {
  const out: Record<string, PermScope> = {};
  catalog.roles
    .filter((r) => roles.includes(r.key))
    .forEach((r) =>
      Object.entries(r.permissions).forEach(([k, s]) => {
        if (!out[k] || RANK[s] > RANK[out[k]]) out[k] = s;
      })
    );
  return out;
};
